import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import L from 'leaflet';
import { X, Layers, Upload, Trash2, Ruler, Satellite, PenLine } from 'lucide-react';
import { compressImage, uid } from '../lib/model';
import { portalRoot } from './TourGuide';
import DecIn from './DecIn';
import { openSketch } from './SketchEditor';
import { mapSnapshot } from '../lib/exporters';
import { modeId, ml } from '../lib/profiles';
import { openRoofFrame } from './RoofFrame';

export const openFloorPlans = () => window.dispatchEvent(new Event('giss-floor-plans'));
export const activePlan = (p) => (p?.plans || []).find((x) => x.id === p.planOn) || null;
// objects of the active storey only (aerial view = everything)
export const onFloor = (p, x) => !p?.planOn || x.floor === p.planOn;
// north-up plan centred on its anchor, size from metres per pixel
export function planBounds(pl) {
  const wm = pl.w * pl.mpp, hm = pl.h * pl.mpp, dLat = hm / 2 / 111320, dLon = wm / 2 / (111320 * Math.cos((pl.lat * Math.PI) / 180));
  return [[pl.lat - dLat, pl.lon - dLon], [pl.lat + dLat, pl.lon + dLon]];
}

export async function pdfToImage(file) {
  const pdfjs = await import('pdfjs-dist/build/pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise, page = await doc.getPage(1);
  const v0 = page.getViewport({ scale: 1 }), vp = page.getViewport({ scale: Math.min(3, 2400 / Math.max(v0.width, v0.height)) });
  const c = document.createElement('canvas'); c.width = vp.width; c.height = vp.height;
  const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
  await page.render({ canvasContext: g, viewport: vp }).promise;
  return c.toDataURL('image/jpeg', 0.85);
}
const dims = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res([i.naturalWidth, i.naturalHeight]); i.onerror = rej; i.src = src; });

// tap two points on the plan and enter their real distance
function ScaleTool({ t, plan, onScale }) {
  const [pts, setPts] = useState([]);
  const [m, setM] = useState(null);
  const tap = (e) => { const r = e.currentTarget.getBoundingClientRect(), p = [((e.clientX - r.left) / r.width) * plan.w, ((e.clientY - r.top) / r.height) * plan.h]; setPts((a) => (a.length >= 2 ? [p] : [...a, p])); };
  const px = pts.length === 2 ? Math.hypot(pts[1][0] - pts[0][0], pts[1][1] - pts[0][1]) : 0;
  return (
    <div className="fp-scale" data-testid={`floor-scale-${plan.id}`}>
      <div className="fp-img-wrap" onClick={tap} data-testid={`floor-scale-img-${plan.id}`}>
        <img src={plan.src} alt="" draggable={false} />
        <svg viewBox={`0 0 ${plan.w} ${plan.h}`} preserveAspectRatio="none">
          {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={plan.w / 90} fill="#ef4444" />)}
          {pts.length === 2 && <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke="#ef4444" strokeWidth={plan.w / 250} />}
        </svg>
      </div>
      <p className="muted small-hint">{t.fpScaleHint}</p>
      <div className="row">
        <label className="pit-num"><span>{t.fpRealM}</span><DecIn data-testid={`floor-scale-m-${plan.id}`} value={m} onNum={setM} /></label>
        <button className="btn btn-green btn-sm" data-testid={`floor-scale-apply-${plan.id}`} disabled={!px || !(m > 0)} onClick={() => { onScale(m / px); setPts([]); }}><Ruler size={14} /> {t.fpApply}</button>
      </div>
    </div>
  );
}

// storeys with an uploaded floor plan (image or PDF page 1), scale by two points
export default function FloorPlans({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scaleId, setScaleId] = useState(null);
  const [name, setName] = useState('');
  const fileRef = useRef(null);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-floor-plans', h); return () => window.removeEventListener('giss-floor-plans', h); }, []);
  if (!on) return null;
  const plans = project.plans || [];
  const setPlan = (id, p) => updateProject((pr) => ({ ...pr, plans: (pr.plans || []).map((x) => (x.id === id ? { ...x, ...p } : x)) }));
  const add = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const src = /pdf$/i.test(file.type) || /\.pdf$/i.test(file.name) ? await pdfToImage(file) : (await compressImage(file, 2400, 0.85, '#ffffff')).data;
      const [w, h] = await dims(src), at = [project, ...(project.reports || [])].find((x) => x?.lat != null) || { lat: 48.137, lon: 11.575 };
      const pl = { id: uid('plan'), name: name.trim() || `${ml(t, 'fpFloor')} ${plans.length + 1}`, src, w, h, mpp: 20 / w, lat: at.lat, lon: at.lon };
      updateProject((pr) => ({ ...pr, plans: [...(pr.plans || []), pl], planOn: pl.id }));
      setName(''); setScaleId(pl.id);
    } catch { toast.error(t.fpFail); }
    setBusy(false);
  };
  const del = (id) => updateProject((pr) => ({ ...pr, plans: (pr.plans || []).filter((x) => x.id !== id), planOn: pr.planOn === id ? null : pr.planOn }));
  // current satellite map view becomes a true-to-scale, georeferenced roof plan
  const fromMap = async () => {
    const v = window.__gissMapView?.();
    if (!v) return;
    setBusy(true);
    let z = Math.min(19, Math.round(v.z));
    while (z > 1 && Math.max(v.s.x, v.s.y) * 2 ** (z - v.z) > 2400) z -= 1;
    const W = Math.round(v.s.x * 2 ** (z - v.z)), H = Math.round(v.s.y * 2 ** (z - v.z));
    const src = await mapSnapshot({ lat: v.c.lat, lon: v.c.lng, zoom: z }, [], { noCross: true }, W, H);
    setBusy(false);
    if (!src) { toast.error(t.fpFail); return; }
    const [w, h] = await dims(src), mpp = (156543.03392 * Math.cos((v.c.lat * Math.PI) / 180)) / 2 ** z;
    const pl = { id: uid('plan'), name: name.trim() || `${t.fpRoofPlan} ${plans.length + 1}`, src, w, h, mpp, lat: v.c.lat, lon: v.c.lng, sat: true };
    updateProject((pr) => ({ ...pr, plans: [...(pr.plans || []), pl], planOn: pl.id }));
    setName(''); toast.success(t.fpFromMapOk); setOn(false);
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="floor-plans-dialog">
      <div className="editor irr-bom">
        <div className="editor-head"><h4><Layers size={18} /> {ml(t, 'fpTitle')}</h4><button className="icon-btn" data-testid="floor-plans-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <div className="row">
          <input className="offer-in" data-testid="floor-name-input" placeholder={ml(t, 'fpNamePh')} value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-green btn-sm" data-testid="floor-upload-btn" disabled={busy} onClick={() => fileRef.current.click()}><Upload size={14} /> {busy ? '…' : t.fpUpload}</button>
          <input ref={fileRef} type="file" hidden accept="image/*,application/pdf" data-testid="floor-file-input" onChange={(e) => { add(e.target.files[0]); e.target.value = ''; }} />
          <button className="btn btn-outline btn-sm" data-testid="floor-sketch-btn" onClick={() => { setOn(false); openSketch(); }}><PenLine size={14} /> {t.psSketch}</button>
          {modeId() === 'detect_roof' && <button className="btn btn-dark btn-sm" data-testid="floor-from-map-btn" disabled={busy} onClick={fromMap}><Satellite size={14} /> {t.fpFromMap}</button>}
          {modeId() === 'detect_roof' && <button className="btn btn-proj btn-sm" data-testid="floor-roof-frame-btn" onClick={() => { setOn(false); openRoofFrame(); }}><Satellite size={14} /> {t.rfBtn}</button>}
        </div>
        {modeId() === 'detect_roof' && <p className="muted small-hint" data-testid="floor-from-map-hint">{t.fpFromMapHint}</p>}
        {!plans.length && <p className="empty" data-testid="floor-empty">{ml(t, 'fpEmpty')}</p>}
        {plans.map((p) => (
          <div key={p.id} className={`irr-zone ${project.planOn === p.id ? '' : 'off'}`} data-testid={`floor-item-${p.id}`}>
            <div className="row">
              <input value={p.name} data-testid={`floor-rename-${p.id}`} onChange={(e) => setPlan(p.id, { name: e.target.value })} />
              <button className="btn btn-outline btn-sm" data-testid={`floor-show-${p.id}`} onClick={() => { updateProject((pr) => ({ ...pr, planOn: p.id })); setOn(false); }}>{t.fpShow}</button>
              {p.sketch
                ? <button className="btn btn-outline btn-sm" data-testid={`floor-sketch-edit-${p.id}`} onClick={() => { setOn(false); openSketch(p.id); }}><PenLine size={14} /></button>
                : <button className="btn btn-outline btn-sm" data-testid={`floor-scale-btn-${p.id}`} onClick={() => setScaleId(scaleId === p.id ? null : p.id)}><Ruler size={14} /></button>}
              <button className="icon-btn danger" data-testid={`floor-del-${p.id}`} onClick={() => del(p.id)}><Trash2 size={14} /></button>
            </div>
            <p className="sw-res" data-testid={`floor-size-${p.id}`}>{t.fpSize}: {String(Math.round(p.w * p.mpp * 10) / 10).replace('.', ',')} × {String(Math.round(p.h * p.mpp * 10) / 10).replace('.', ',')} m</p>
            {scaleId === p.id && <ScaleTool t={t} plan={p} onScale={(mpp) => { setPlan(p.id, { mpp }); toast.success(t.fpScaled); }} />}
          </div>
        ))}
        <p className="muted small-hint">{ml(t, 'fpHint')}</p>
      </div>
    </div>,
    portalRoot(),
  );
}

// storey switcher on the map
export function FloorBar({ t, project, updateProject }) {
  const plans = project?.plans || [];
  if (!plans.length) return null;
  const set = (id) => updateProject((pr) => ({ ...pr, planOn: id }));
  return (
    <div className="floor-bar" data-testid="floor-bar">
      <button className={!project.planOn ? 'on' : ''} data-testid="floor-bar-aerial" onClick={() => set(null)}><Satellite size={13} /> {t.fpAerial}</button>
      {plans.map((p) => <button key={p.id} className={project.planOn === p.id ? 'on' : ''} data-testid={`floor-bar-${p.id}`} onClick={() => set(p.id)}>{p.name}</button>)}
    </div>
  );
}

// map: active plan as image, satellite hidden behind it
export function useFloorPlanLayer(mapRef, plan) {
  const key = plan ? `${plan.id}|${plan.mpp}|${plan.lat}|${plan.lon}` : '';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !plan) return undefined;
    const b = planBounds(plan), ov = L.imageOverlay(plan.src, b, { opacity: 1, interactive: false, className: 'floor-plan-img' }).addTo(map);
    ov.bringToBack();
    map.getContainer().classList.add('floor-mode');
    if (!plan.sat) map.fitBounds(b, { padding: [20, 20] });
    return () => { ov.remove(); map.getContainer().classList.remove('floor-mode'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, key]);
}
